import {Page} from '@PosterWhiteboard/page/page.class';
import type {PosterPageObject} from '@PosterWhiteboard/poster/poster.types';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {PageObject} from '@PosterWhiteboard/page/page.types';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import type {DeepPartial} from '@/global';
import {noop} from '@Utils/general.util';
import {isFabricObjectOnPageCanvas} from '@Components/poster-editor/library/poster-editor-library';
import type {FabricObject} from '@postermywall/fabricjs-2';

interface AddPageOpts {
  undoable?: boolean;
  updateRedux?: boolean;
  pageOrder?: number;
  onItemAddFail?: () => void;
}

export class PosterPages {
  public pagesHashMap: Record<string, Page> = {};
  public pageOrder: string[] = [];
  public poster: Poster;
  private readonly boundOnPageTimeUpdated = this.onPageTimeUpdated.bind(this);

  public constructor(poster: Poster) {
    this.poster = poster;
    this.poster.on('time:updated', this.boundOnPageTimeUpdated);
  }

  public onPageTimeUpdated(time: number): void {
    this.updateSelectedPageForTime(time);
  }

  public async addPage(pageObject: DeepPartial<PageObject> = {}, {updateRedux = true, undoable = true, pageOrder, onItemAddFail = noop}: AddPageOpts = {}): Promise<Page> {
    const page = new Page(this.poster, pageObject.hashedID, pageOrder);
    page.applyScale();
    this.pagesHashMap[page.hashedID] = page;

    const filteredPageOrder = this.pageOrder.filter((id) => {
      return id !== page.hashedID;
    });
    if (pageOrder !== undefined && pageOrder <= filteredPageOrder.length) {
      filteredPageOrder.splice(pageOrder, 0, page.hashedID);
      this.pageOrder = filteredPageOrder;
    } else {
      this.pageOrder = [...filteredPageOrder, page.hashedID];
    }

    if (page.poster.getCurrentPageId() === '') {
      page.poster.setCurrentPage(page.hashedID);
    }

    await page.updateFromObject(pageObject, {undoable: false, updateRedux: false, updateItemsDuration: false, onItemAddFail});

    this.initSelectionEvents(page);

    if (undoable) {
      this.poster.history.addPosterHistory();
    }
    if (updateRedux) {
      this.poster.redux.updateReduxData();
    }
    return page;
  }

  private initSelectionEvents(newlyAddedPage: Page): void {
    newlyAddedPage.fabricCanvas.on('selection:created', () => {
      for (const page of Object.values(this.pagesHashMap)) {
        if (page.hashedID !== newlyAddedPage.hashedID) {
          page.clearSelection();
        }
      }
    });

    newlyAddedPage.fabricCanvas.on('mouse:down', () => {
      for (const page of Object.values(this.pagesHashMap)) {
        if (page.hashedID !== newlyAddedPage.hashedID) {
          page.clearSelection();
        }
      }
    });

    newlyAddedPage.fabricCanvas.on('selection:updated', () => {
      for (const page of Object.values(this.pagesHashMap)) {
        if (page.hashedID !== newlyAddedPage.hashedID) {
          page.clearSelection();
        }
      }
    });
  }

  public movePageUp(pageId: string): void {
    this.movePageToIndex(pageId, this.pageOrder.indexOf(pageId) - 1);
  }

  public movePageDown(pageId: string): void {
    this.movePageToIndex(pageId, this.pageOrder.indexOf(pageId) + 1);
  }

  public movePageToIndex(pageId: string, newIndex: number): void {
    const newPagesOrder = [...this.pageOrder];
    const oldIndex = this.pageOrder.indexOf(pageId);
    newPagesOrder.splice(oldIndex, 1);
    newPagesOrder.splice(newIndex, 0, pageId);

    this.pageOrder = newPagesOrder;
    this.refreshPagesHtmlOrder();
    this.poster.refreshAllVisibleCanvases();
    void this.seekToPageIndex(newIndex);

    this.poster.history.addPosterHistory();
    this.poster.redux.updateReduxData();
  }

  public toObject(): PosterPageObject {
    const pageObjects: Record<string, PageObject> = {};

    for (const [key, page] of Object.entries(this.pagesHashMap)) {
      pageObjects[key] = page.toObject();
    }

    return {
      pagesHashMap: pageObjects,
      pageOrder: this.pageOrder,
    };
  }

  public getTotalDuration(): number {
    let totalDuration = 0;
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      totalDuration += page.getDuration();
    }
    return totalDuration;
  }

  public refreshPagesHtmlOrder(): void {
    const parent = document.querySelector('.pmw-page-container-parent');

    if (parent) {
      const containerMap = new Map<string, HTMLElement>();

      const containers = parent.querySelectorAll<HTMLElement>('.pmw-page-container');
      containers.forEach((container) => {
        const dataId = container.getAttribute('data-id');
        if (dataId) {
          containerMap.set(dataId, container);
        }
      });

      this.pageOrder.forEach((id) => {
        const el = containerMap.get(id);
        if (el) {
          parent.appendChild(el);
        }
      });
    }
  }

  public async updateFromObject(
    posterPagesObject: DeepPartial<PosterPageObject>,
    {updateRedux = true, fireModifiedEvent = true, undoable = true, refreshActiveSelection = false, checkForDurationUpdate = false, onItemAddFail = noop}: UpdateFromObjectOpts = {}
  ): Promise<void> {
    if (!posterPagesObject.pagesHashMap) {
      return;
    }

    // Update/Add pages from pagesObject
    const promises = [];
    for (const [hashId, pageObject] of Object.entries(posterPagesObject.pagesHashMap)) {
      if (this.pagesHashMap[hashId] !== undefined) {
        promises.push(
          this.pagesHashMap[hashId].updateFromObject(pageObject, {
            updateRedux: false,
            fireModifiedEvent: true,
            updateItemsDuration: false,
            firePosterModifiedEvent: false,
            undoable: false,
            refreshActiveSelection,
            onItemAddFail,
            checkForDurationUpdate,
          })
        );
      } else {
        promises.push(this.addPage(pageObject, {updateRedux: false, undoable: false, pageOrder: posterPagesObject.pageOrder?.indexOf(hashId), onItemAddFail}));
      }
    }

    // delete pages that are in the poster but not in the pagesObject
    for (const [hashId] of Object.entries(this.pagesHashMap)) {
      if (posterPagesObject.pagesHashMap[hashId] === undefined) {
        promises.push(this.doRemovePage(hashId));
      }
    }
    await Promise.all(promises);

    if (undoable) {
      this.poster.history.addPosterHistory();
    }
    if (fireModifiedEvent) {
      this.poster.fire('modified');
    }
    if (updateRedux) {
      this.poster.redux.updateReduxData();
    }
  }

  public async removePage(pageUid: string): Promise<void> {
    if (this.hasPage(pageUid)) {
      await this.doRemovePage(pageUid);
      this.poster.fire('modified');
      this.poster.history.addPosterHistory();
      this.poster.redux.updateReduxData();
      this.poster.refreshAllVisibleCanvases();
    }
  }

  public hasPage(hashId: string): boolean {
    return this.pagesHashMap[hashId] !== undefined;
  }

  public getPage(hashId: string): Page | undefined {
    if (!this.hasPage(hashId)) {
      return undefined;
    }

    return this.pagesHashMap[hashId];
  }

  public applyScales(): void {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      page.applyScale();
    }
  }

  public convertTransparentBackgroundsToSolid(): void {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      if (page.background.isTransparentBackground()) {
        page.background.updateBackgroundToBeNonTransparent();
      }
    }
  }

  public getItemForId(uid: string): ItemType | undefined {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      if (page.items.hasItem(uid)) {
        return page.items.getItem(uid);
      }
    }
    return undefined;
  }

  public getPageWithItemId(uid: string): Page | undefined {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      if (page.items.hasItem(uid)) {
        return page;
      }
    }
    return undefined;
  }

  public hasMultiplePages(): boolean {
    return Object.keys(this.pagesHashMap).length > 1;
  }

  public hasVideoPage(): boolean {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      if (page.isVideo()) {
        return true;
      }
    }

    return false;
  }

  public isPremium(): boolean {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      if (page.isPremium()) {
        return true;
      }
    }

    return false;
  }

  public async pause(): Promise<void> {
    const promises = [];
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      promises.push(page.pause());
    }
    await Promise.all(promises);
  }

  public async stop(): Promise<void> {
    const promises = [];
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      promises.push(page.stop());
    }
    await Promise.all(promises);
  }

  public clearSelection(): void {
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      page.clearSelection();
    }
  }

  public dispose(): void {
    this.poster.off('time:updated', this.boundOnPageTimeUpdated);
    for (const [, page] of Object.entries(this.pagesHashMap)) {
      page.dispose().catch(console.error);
    }
  }

  private async doRemovePage(hashId: string): Promise<void> {
    if (this.hasPage(hashId)) {
      await this.pagesHashMap[hashId].dispose();
      const newCurrentPageIdIfCurrentPageDeleted = this.getAdjacentPageIdOnPageRemoved(this.poster.getCurrentPageId());
      // this.page.fabricCanvas.remove(this.pagesHashMap[itemUid].fabricObject);
      delete this.pagesHashMap[hashId];
      this.pageOrder = this.pageOrder.filter((id) => {
        return id !== hashId;
      });
      if (this.poster.getCurrentPageId() === hashId) {
        this.poster.setSelectedPageAndUpdateView(newCurrentPageIdIfCurrentPageDeleted);
        const newCurrentPageOrder = this.pageOrder.indexOf(newCurrentPageIdIfCurrentPageDeleted);
        void this.poster.pages.seekToPageIndex(newCurrentPageOrder);
      }
    }
  }

  public getAdjacentPageIdOnPageRemoved(hashId: string): string {
    const pageOrder = this.pageOrder.indexOf(hashId);
    const adjacentPageOrder = pageOrder === 0 ? 1 : pageOrder - 1;
    return this.pageOrder[adjacentPageOrder];
  }

  public getPageOrder(pageHashId: string): number | undefined {
    const pageOrder = this.pageOrder.indexOf(pageHashId);
    return pageOrder !== -1 ? pageOrder : undefined;
  }

  public getPagesInOrder(): Page[] {
    const pages = [];
    for (const pageId of this.pageOrder) {
      pages.push(this.pagesHashMap[pageId]);
    }

    return pages;
  }

  public getNumberOfPages(): number {
    return Object.keys(this.pagesHashMap).length;
  }

  public showPage(pageId: string): void {
    const pageElement = Page.getHTMLContainerForPageId(pageId);
    pageElement?.classList.remove('_hidden');
  }

  public hidePage(pageId: string): void {
    const pageElement = Page.getHTMLContainerForPageId(pageId);
    pageElement?.classList.add('_hidden');
  }

  public getPreviousPageIndex(): number | undefined {
    const previousSlideIndex = (this.getPageOrder(this.poster.getCurrentPageId()) ?? 0) - 1;
    return previousSlideIndex >= 0 ? previousSlideIndex : undefined;
  }

  public getNextPageIndex(): number | undefined {
    const nextPageIndex = (this.getPageOrder(this.poster.getCurrentPageId()) ?? 0) + 1;
    return nextPageIndex < Object.keys(this.pagesHashMap).length ? nextPageIndex : undefined;
  }

  public async seekToPageIndex(pageIndex: number): Promise<void> {
    if (pageIndex !== undefined) {
      await this.seekToPage(this.pagesHashMap[this.pageOrder[pageIndex]].hashedID);
    }
  }

  public async seekToPage(pageId: string): Promise<void> {
    const pageTime = this.getStartTimeForPage(pageId);
    await this.poster.seek(pageTime);
  }

  public getStartTimeForPage(pageId: string): number {
    let startTime = 0;
    for (const id of this.pageOrder) {
      if (pageId === id) {
        return startTime;
      }
      startTime += this.pagesHashMap[id].getDuration();
    }
    throw new Error(`No page in pageOrder for id ${pageId}`);
  }

  public findPageContainingItem(fabricObject: FabricObject): Page | null {
    for (const page of Object.values(this.pagesHashMap)) {
      if (isFabricObjectOnPageCanvas(fabricObject, page.hashedID)) {
        return page;
      }
    }

    return null;
  }

  public getEndTimeForPage(pageId: string): number {
    return this.getStartTimeForPage(pageId) + this.pagesHashMap[pageId].getDuration();
  }

  protected getPageIndexForTime(time: number): number {
    const numberOfPages = Object.keys(this.pagesHashMap).length;
    const posterDuration = this.poster.getDuration();
    let posterTime = 0;

    let index = 0;

    for (const id of this.pageOrder) {
      const page = this.pagesHashMap[id];
      posterTime += page.getDuration();
      if (posterTime > time) {
        return index;
      }
      if (time >= posterDuration) {
        return numberOfPages - 1;
      }
      index += 1;
    }
    return index;
  }

  public updateSelectedPageForTime(time: number): void {
    const pageIndex = this.getPageIndexForTime(time);
    const page = this.pagesHashMap[this.pageOrder[pageIndex]];
    const currentPage = this.poster.getCurrentPage();

    if (page && currentPage.hashedID !== page.hashedID) {
      currentPage.clearSelection();
      this.switchToPage(page.hashedID);
    }
  }

  public switchToPage(pageId: string): void {
    this.poster.setSelectedPageAndUpdateView(pageId);
    void this.pagesHashMap[pageId].playPage();
  }
}
